import Papa from 'papaparse';
import { read as XLSXRead, utils as XLSXUtils } from 'xlsx';

const UTF8_BOM = [0xef, 0xbb, 0xbf] as const;

function bytesWithoutUtf8Bom(bytes: Uint8Array): Uint8Array {
  if (
    bytes.length >= 3 &&
    bytes[0] === UTF8_BOM[0] &&
    bytes[1] === UTF8_BOM[1] &&
    bytes[2] === UTF8_BOM[2]
  ) {
    return bytes.subarray(3);
  }

  return bytes;
}

/**
 * UTF-8 first, then Windows-1252. Excel on Windows often re-saves a CSV as
 * Windows-1252; UTF-8-only decoding would turn a string like "Gültig ab" into a header
 * that no longer matches the default column map.
 */
function decodeTextFileBytes(bytes: Uint8Array): string {
  const payload = bytesWithoutUtf8Bom(bytes);

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(payload);
  } catch {
    return new TextDecoder('windows-1252').decode(payload);
  }
}

/**
 * Utility function to read a file as text asynchronously.
 * @param file - The file to be read.
 * @returns A promise that resolves with the file content as text.
 */
export const readFileAsTextAsync = async (file: File | undefined) => {
  if (!file) {
    throw new Error('No file selected. Please choose a file.');
  }

  const isJsonFile = file.type === 'application/json';
  const isTextFile = file.type.startsWith('text');

  if (!isJsonFile && !isTextFile) {
    throw new Error(
      `Unsupported file type "${file.type}". Please select a text file.`,
    );
  }

  try {
    const bytes = new Uint8Array(await file.arrayBuffer());

    return decodeTextFileBytes(bytes).trim();
  } catch (error) {
    throw new Error(
      `Failed to read file: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Utility function to read a CSV file asynchronously.
 * @param file - The file to be read.
 * @param options - The options to be passed to the Papa.parse function.
 * @returns A promise that resolves with the file content as JSON.
 */
export const readCsvFileAsync = async (
  file: File,
  options: Papa.ParseConfig,
) => {
  if (!file) {
    throw new Error('No file selected. Please choose a file.');
  }

  const text = await readFileAsTextAsync(file);

  const parsed = Papa.parse(text, {
    header: true,
    skipEmptyLines: true,
    ...options,
  });

  return parsed;
};

export type ReadXlsxFileOptions = {
  /**
   * Selects a sheet by name or by zero-based index. Defaults to the
   * workbook's first sheet.
   */
  sheet?: string | number;
};

/**
 * Utility function to read an XLSX file asynchronously.
 *
 * Date cells stay Excel serial numbers in `data`; converting them is the
 * consumer's job (see the import schemas' date normalization). `displayData`
 * carries the same rows formatted with each cell's number format, for
 * showing values the way the spreadsheet displayed them.
 *
 * @param file - The file to be read.
 * @param options - Optional sheet selector; defaults to the first sheet.
 * @returns Raw rows, source-formatted display rows, and headers from the selected sheet.
 */
export const readXlsxFileAsync = async (
  file: File,
  options: ReadXlsxFileOptions = {},
) => {
  if (!file) {
    throw new Error('No file selected. Please choose a file.');
  }

  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('Unsupported file type. Please select an XLSX file.');
  }

  try {
    const content = await file.arrayBuffer();

    const workbook = XLSXRead(content, { type: 'buffer' });
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error('No sheets found in the XLSX file.');
    }

    const sheetName =
      typeof options.sheet === 'number'
        ? workbook.SheetNames[options.sheet]
        : (options.sheet ?? workbook.SheetNames[0]);

    if (!sheetName || !workbook.SheetNames.includes(sheetName)) {
      throw new Error(
        `Sheet "${String(options.sheet)}" not found in the XLSX file.`,
      );
    }

    const sheet = workbook.Sheets[sheetName];

    if (!sheet) {
      throw new Error('Unable to read sheet from the XLSX file.');
    }

    // Get headers from the first row
    const headersRow = XLSXUtils.sheet_to_json(sheet, {
      raw: false,
      header: 1,
      range: 0,
    })[0] as string[];

    const data = XLSXUtils.sheet_to_json<Record<string, unknown>>(sheet, {
      raw: true,
      defval: '',
    });
    const displayData = XLSXUtils.sheet_to_json<Record<string, unknown>>(
      sheet,
      {
        raw: false,
        defval: '',
      },
    );

    return {
      data,
      displayData,
      headers: headersRow,
    };
  } catch (error) {
    throw new Error(
      `Failed to read XLSX file: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};
